import { initializeApp, getApp, getApps } from 'firebase/app'
import {
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { Capacitor } from '@capacitor/core'
import { getFirebaseConfig, hasRequiredFirebaseConfig } from '../../packages/firebase/config'
import { getOrInitializeAuth } from '../../packages/firebase/authSingleton'

const CORE_CONFIG = getFirebaseConfig()

const hasConfig = hasRequiredFirebaseConfig(CORE_CONFIG)

// 이 모듈은 "기본 app"만 소유한다. getApps()[0]으로 조회하면 안 된다 —
// 관리자 웹 번들처럼 이름 있는 app('suguide-admin')이 먼저 등록된 곳에서는
// getApps()[0]이 그 관리자 app을 돌려주고, 아래 auth 초기화가 관리자 app에
// 두 번째 initializeAuth를 걸어 auth/already-initialized로 화면이 죽는다.
// (admin이 src/services/shuttleCmsService.js → 이 모듈을 import하면서 같은 번들에 실린다.)
const DEFAULT_APP_NAME = '[DEFAULT]'

export const firebaseApp = hasConfig
  ? (getApps().some(app => app.name === DEFAULT_APP_NAME) ? getApp() : initializeApp(CORE_CONFIG))
  : null

/**
 * Firebase Authentication (미설정 시 null — 로그인 기능 비활성화)
 *
 * getAuth() 대신 initializeAuth()로 명시 초기화한다.
 * getAuth()는 기본 popupRedirectResolver(browserPopupRedirectResolver)를 설치하는데,
 * 이 resolver의 eager 초기화가 Capacitor iOS(capacitor://localhost 커스텀 스킴)에서
 * authDomain의 OAuth iframe(https://<authDomain>/__/auth/iframe) 로드를 시도하다
 * 무한 대기에 빠진다. 그 결과 auth._initializationPromise가 영원히 pending되어
 * signInWithCustomToken 이 resolve/reject 없이 hang(카카오 네이티브 로그인 무한 pending의 원인).
 *
 * → 네이티브에서는 resolver를 주입하지 않아 초기화가 즉시 끝나게 하고,
 *   persistence만 [indexedDBLocalPersistence, browserLocalPersistence]로 명시한다.
 *   웹은 Google/Apple 팝업 로그인(signInWithPopup)에 resolver가 필요하므로 유지한다.
 *   (네이티브 Google/Apple/Kakao는 popup 경로를 쓰지 않음 → resolver 불필요)
 *
 * Auth 인스턴스는 프로젝트 전체에서 이 파일에서만 1회 생성·export한다(getAuth 혼용 금지).
 */
export const auth = getOrInitializeAuth(firebaseApp, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
  ...(Capacitor.isNativePlatform()
    ? {}
    : { popupRedirectResolver: browserPopupRedirectResolver }),
})

/** Firestore Database (미설정 시 null — 데이터 마이그레이션/동기화 비활성화) */
export const db = firebaseApp ? getFirestore(firebaseApp) : null
